/**
 * Prepares data for Firestore by ensuring all date fields use the normalized YYYY-MM-DD format
 * Use this before writing data to Firestore
 * @param {Object} data - The data object to prepare for Firestore
 * @returns {Object} The prepared data with normalized dates
 */
export const prepareDataForFirestore = (data) => {
    return normalizeDataDates(data);
  };/**
   * Normalizes date strings to YYYY-MM-DD format
   * Handles both ISO date strings and simple date strings
   * @param {string|Date} dateInput - The date string or Date object to normalize
   * @returns {string} Normalized date in YYYY-MM-DD format
   */
  export const normalizeDate = (dateInput) => {
    if (!dateInput) return '';
    
    try {
      // Handle Date objects directly
      const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
      if (isNaN(date.getTime())) return String(dateInput); // Invalid date, return as is
      
      // Extract year, month, and day
      const year = date.getFullYear();
      // getMonth() is 0-indexed, so add 1 and pad with leading zero if needed
      const month = String(date.getMonth() + 1).padStart(2, '0');
      // getDate() returns the day of the month, pad with leading zero if needed
      const day = String(date.getDate()).padStart(2, '0');
      
      // Return in YYYY-MM-DD format
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error normalizing date:', error);
      return String(dateInput); // Return original if there's an error
    }
  };
  
  /**
   * Formats a date string for display in the UI
   * @param {string} dateString - The date string to format
   * @returns {string} Formatted date string (e.g., "Mon, Jun 2")
   */
  export const formatDateForDisplay = (dateInput) => {
    if (!dateInput) return '';
  
    try {
      const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
      if (isNaN(date.getTime())) return String(dateInput); // Invalid date
  
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date for display:', error);
      return String(dateInput);
    }
  };
  
  
  /**
   * Converts ISO date strings to simple YYYY-MM-DD format in objects
   * Recursively processes nested objects and arrays
   * @param {Object|Array} data - The data structure to process
   * @returns {Object|Array} Processed data with normalized dates
   */
  export const normalizeDataDates = (data) => {
    if (!data) return data;
    
    // For arrays, process each item
    if (Array.isArray(data)) {
      return data.map(item => normalizeDataDates(item));
    }
    
    // For objects, process each property
    if (typeof data === 'object' && data !== null) {
      const result = { ...data };
      
      for (const [key, value] of Object.entries(result)) {
        // If the key is 'date' and the value is a string, normalize it
        if (key === 'date' && typeof value === 'string') {
          result[key] = normalizeDate(value);
        } 
        // For nested objects or arrays, recursively process them
        else if (typeof value === 'object' && value !== null) {
          result[key] = normalizeDataDates(value);
        }
      }
      
      return result;
    }
    
    return data;
  };